// Wires a DD/MM/YYYY input group: auto-advances to the next field once a part
// is full, moves back to the previous field on Backspace when the current one
// is empty, and keeps a hidden ISO (YYYY-MM-DD) input in sync for submission.
// Returns the [day, month, year] inputs so callers can toggle `required`.
function bindDobGroup(card) {
  const group = card.querySelector('[data-dob-group]');
  const hiddenInput = card.querySelector('[data-dob-input]');
  const parts = Array.from(group.querySelectorAll('[data-dob-part]'));

  function syncHidden() {
    const [day, month, year] = parts;
    if (day.value && month.value && year.value.length === 4) {
      hiddenInput.value = `${year.value}-${month.value.padStart(2, '0')}-${day.value.padStart(2, '0')}`;
    } else {
      hiddenInput.value = '';
    }
  }

  parts.forEach((input, i) => {
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, input.maxLength);
      if (input.value.length >= input.maxLength && i < parts.length - 1) {
        parts[i + 1].focus();
      }
      syncHidden();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && input.value === '' && i > 0) {
        event.preventDefault();
        parts[i - 1].focus();
      }
    });
  });

  syncHidden();
  return parts;
}

class RemindersForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.peopleList = this.querySelector('[data-people-list]');
    this.template = this.querySelector('[data-person-card-template]');
    this.addPersonButton = this.querySelector('[data-add-person]');
    this.status = this.querySelector('[data-form-status]');
    this.successPanel = this.querySelector('[data-form-success]');
    this.successHeading = this.querySelector('[data-success-heading]');
    this.successBody = this.querySelector('[data-success-body]');
    this.nextIndex = this.peopleList.querySelectorAll('[data-person-card]').length;

    this.bindExistingCard(this.peopleList.querySelector('[data-person-card]'));
    this.addPersonButton.addEventListener('click', this.onAddPerson.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.prefillFromQuery();
  }

  // Reads params off the real browser URL rather than server-rendered Liquid --
  // this is a plain (non-customer-scoped) page, so Shopify's CDN can cache the
  // HTML and bake in stale/blank values (same issue fixed on the verify page).
  // Only fills the first person card: this is meant for a customer arriving
  // from the checkout thank-you page reminder link for the item they just
  // bought, not for people added afterwards via "+ Add another person".
  prefillFromQuery() {
    const params = new URLSearchParams(window.location.search);

    const email = params.get('email');
    const emailInput = this.form.querySelector('#RemindersForm-email');
    if (email && emailInput && !emailInput.value) {
      emailInput.value = email;
    }

    const variant = params.get('variant');
    const variantSelect = this.peopleList.querySelector('[name="people[0][variant]"]');
    if (variant && variantSelect && Array.from(variantSelect.options).some((o) => o.value === variant)) {
      variantSelect.value = variant;
    }
  }

  onAddPerson() {
    const fragment = this.template.content.cloneNode(true);
    const card = fragment.querySelector('[data-person-card]');
    this.reindexCard(card, this.nextIndex);
    this.nextIndex += 1;
    card.querySelector('[data-remove-person]').hidden = false;
    this.bindExistingCard(card);
    this.peopleList.appendChild(fragment);
  }

  bindExistingCard(card) {
    if (!card) return;
    const removeButton = card.querySelector('[data-remove-person]');
    removeButton.addEventListener('click', () => card.remove());

    const dobParts = bindDobGroup(card);

    const birthdayCheckbox = card.querySelector('[data-occasion="birthday"]');
    const dobField = card.querySelector('[data-dob-field]');
    birthdayCheckbox.addEventListener('change', () => {
      dobField.hidden = !birthdayCheckbox.checked;
      dobParts.forEach((input) => {
        input.required = birthdayCheckbox.checked;
      });
    });
  }

  reindexCard(card, index) {
    card.dataset.index = index;
    card.querySelectorAll('[id], [name]').forEach((el) => {
      if (el.id) el.id = el.id.replace(/-\d+-/, `-${index}-`);
      if (el.name) el.name = el.name.replace(/^people\[\d+\]/, `people[${index}]`);
    });
    card.querySelectorAll('[for]').forEach((el) => {
      el.setAttribute('for', el.getAttribute('for').replace(/-\d+-/, `-${index}-`));
    });
    const heading = card.querySelector('.reminders-person-card__heading');
    if (heading) heading.textContent = heading.textContent.replace(/\d+/, index + 1);
  }

  collectPeople() {
    return Array.from(this.peopleList.querySelectorAll('[data-person-card]')).map((card) => {
      const field = (name) => card.querySelector(`[name$="[${name}]"]`);
      return {
        name: field('name').value.trim(),
        variant: field('variant').value,
        date_of_birth: field('date_of_birth').value || null,
        reminds_birthday: field('reminds_birthday').checked,
        reminds_christmas: field('reminds_christmas').checked,
        reminds_mothers_day: field('reminds_mothers_day').checked,
      };
    });
  }

  showStatus(message, isError) {
    this.status.textContent = message;
    this.status.hidden = false;
    this.status.classList.toggle('reminders-form__status--error', Boolean(isError));
  }

  showSuccess(heading, body, email) {
    this.form.hidden = true;
    this.successHeading.textContent = heading;

    this.successBody.textContent = '';
    const [before, after] = body.split('%%EMAIL%%');
    this.successBody.appendChild(document.createTextNode(before ?? ''));
    if (email) {
      const strong = document.createElement('strong');
      strong.textContent = email;
      this.successBody.appendChild(strong);
    }
    this.successBody.appendChild(document.createTextNode(after ?? ''));

    this.successPanel.hidden = false;
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    const submitButton = this.form.querySelector('button[type="submit"]');
    submitButton.disabled = true;
    submitButton.classList.add('loading');
    submitButton.querySelector('.loading__spinner').classList.remove('hidden');

    const email = this.form.querySelector('#RemindersForm-email').value.trim();
    const payload = {
      email,
      consent: true,
      people: this.collectPeople(),
    };

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: this.dataset.publicKey,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || result.status === 'error') {
        this.showStatus(this.dataset.errorMessage, true);
        return;
      }

      if (result.verified) {
        this.showSuccess(this.dataset.successVerifiedHeading, this.dataset.successVerifiedBody, email);
      } else {
        this.showSuccess(this.dataset.successUnverifiedHeading, this.dataset.successUnverifiedBody, email);
      }
    } catch (error) {
      this.showStatus(this.dataset.errorMessage, true);
    } finally {
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
      submitButton.querySelector('.loading__spinner').classList.add('hidden');
    }
  }
}

customElements.define('reminders-form', RemindersForm);
